<script lang="ts">
  import { onMount } from 'svelte'
  import Portal from 'svelte-portal'
  import SourcePicker from './SourcePicker.svelte'
  import WebhookConfig from './sources/WebhookConfig.svelte'
  import EmailConfig from './sources/EmailConfig.svelte'
  import WeatherConfig from './sources/WeatherConfig.svelte'

  interface Props {
    hashid: string | null
  }

  let { hashid }: Props = $props()

  const isNew = hashid === null

  const MAX_GROUPS = 10

  // ── Form state ────────────────────────────────────────────────────────────
  let name = $state('')
  let groupIds = $state<number[]>([])
  let groupPickerOpen = $state(false)
  let reviewerId = $state<number | null>(null)
  let prompt = $state('')
  let audienceDescription = $state('')
  let active = $state(true)
  let sourceType = $state<'webhook' | 'weather' | 'email'>('email')
  let sourceConfig = $state<Record<string, unknown>>({})

  const promptPlaceholder = $derived(
    sourceType === 'weather'
      ? "e.g. When a winter storm warning arrives, tell parents school is closed today and buses aren't running. Mention the storm name if available."
      : sourceType === 'email'
      ? "e.g. When you receive a school closure email, draft a brief, calm SMS to parents. Keep the date and reason from the email."
      : "e.g. Draft a short SMS from the request payload. Include the event name and any severity or location fields when present."
  )

  // ── Metadata ─────────────────────────────────────────────────────────────
  let inboundEmailAddress = $state<string | null>(null)
  let webhookUrl = $state('')
  let signingSecret = $state('')
  let previousSigningSecret = $state<string | null>(null)
  let previousSecretExpiresAtIso = $state<string | null>(null)
  let previousSecretActive = $state(false)
  let secretVisible = $state(false)
  let copiedWebhook = $state(false)
  let copiedSecret = $state(false)
  let showRegenerateConfirm = $state(false)
  let regenerating = $state(false)
  let expiringPreviousSecret = $state(false)
  let showDeleteConfirm = $state(false)
  let deleting = $state(false)
  let deleteError = $state<string | null>(null)

  // ── Recent fires ──────────────────────────────────────────────────────────
  interface FireRow {
    id: string
    status: string
    fired_at_iso: string
    message_preview: string | null
  }
  let recentFires = $state<FireRow[]>([])

  // ── Select options ────────────────────────────────────────────────────────
  interface GroupOption  { id: number; name: string; is_all_group?: boolean }
  interface ReviewerOption { id: number; email: string; name: string }

  // "All" is exclusive — selecting it replaces any other selections, and
  // selecting another group while All is selected replaces All.
  function addGroup(id: number) {
    const picked = availableGroups.find((g) => g.id === id)
    if (!picked) return
    if (picked.is_all_group) {
      groupIds = [id]
      return
    }
    const filtered = groupIds.filter((gid) => {
      const g = availableGroups.find((x) => x.id === gid)
      return g && !g.is_all_group
    })
    if (filtered.length < MAX_GROUPS) groupIds = [...filtered, id]
  }

  const allGroupSelected = $derived(
    groupIds.some((id) => availableGroups.find((g) => g.id === id)?.is_all_group)
  )

  let availableGroups = $state<GroupOption[]>([])
  let availableReviewers = $state<ReviewerOption[]>([])

  // ── UI state ──────────────────────────────────────────────────────────────
  let loading = $state(!isNew)
  let fetchError = $state<string | null>(null)
  let submitting = $state(false)
  let submitError = $state<string | null>(null)

  function csrfToken(): string {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? ''
  }

  // ── Load existing trigger (edit mode) ─────────────────────────────────────
  onMount(async () => {
    if (isNew) {
      // For new mode, we still need available groups/reviewers — fetch from index
      try {
        const res = await fetch('/triggers.json', {
          headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
        })
        // We don't need the list itself, just fetch one show if possible
        // For new, we'll get groups/reviewers via a dummy fetch approach
        // Actually let's get them from a show request... but there's no trigger yet.
        // Instead, rely on the fact that any existing trigger show would have them.
        // For new mode: make an extra fetch if there's at least one trigger, otherwise
        // fall back gracefully. Better: always fetch from the groups endpoint via the
        // show response shape. We'll just skip loading groups/reviewers for now if no
        // triggers exist — the backend show endpoint has them.
        //
        // Simpler: expose a /triggers/new.json endpoint or just load data from an
        // existing trigger. Given the spec says show includes available_groups and
        // available_reviewers, and for "new" there's no trigger to show, we'll
        // call a small helper route. Since we don't want to add routes, we'll fetch
        // from /triggers.json and if the first trigger exists, use its show.
        //
        // Actually the cleanest approach: call /triggers/options.json or just
        // always have the index JSON include available options. Let's add a
        // query param approach: /triggers.json?with_options=1
        //
        // For now, use a pragmatic approach: on new mode, try to get options
        // by loading from /triggers.json with a flag. If 0 triggers, options are
        // empty and the user sees empty selects (acceptable MVP).
        //
        // Actually the simplest: fetch a special metadata endpoint. Since we control
        // the controller, let's just make /triggers/new.json return available options.
        // BUT we can't add routes without changing the controller. We CAN just add
        // format.json to the new action. Let's do that properly via the controller —
        // the `new` action should return options JSON for XHR requests.
        //
        // For now, calling /triggers.json handles options if any trigger exists.
        // We call show on the first trigger to get options. If no triggers exist,
        // we can't get options without a dedicated endpoint.
        //
        // REVISED PLAN: Add a fetch to /triggers/form_options.json by making the
        // new action respond to JSON with available_groups and available_reviewers.
        // We'll call /triggers/new.json from Svelte.
        const optRes = await fetch('/triggers/new.json', {
          headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
        })
        if (optRes.ok) {
          const optData = await optRes.json()
          availableGroups = optData.available_groups ?? []
          availableReviewers = optData.available_reviewers ?? []
          if (availableGroups.length > 0) groupIds = [availableGroups[0].id]
          if (availableReviewers.length > 0) reviewerId = availableReviewers[0].id
        }
      } catch {
        // Non-fatal for new mode
      }
      loading = false
      return
    }

    try {
      const res = await fetch(`/triggers/${hashid}.json`, {
        headers: { Accept: 'application/json', 'X-CSRF-Token': csrfToken() },
      })
      if (!res.ok) {
        fetchError = `Failed to load trigger (${res.status})`
        return
      }
      const data = await res.json()
      const t = data.trigger
      name = t.name
      groupIds = Array.isArray(t.group_ids) ? [...t.group_ids] : []
      reviewerId = t.reviewer_id
      prompt = t.prompt
      audienceDescription = t.audience_description ?? ''
      active = t.active
      sourceType = t.source_type ?? 'webhook'
      sourceConfig = t.source_config ?? {}
      inboundEmailAddress = t.inbound_email_address ?? null
      webhookUrl = t.webhook_url
      signingSecret = t.signing_secret
      previousSigningSecret = t.previous_signing_secret ?? null
      previousSecretExpiresAtIso = t.previous_secret_expires_at_iso ?? null
      previousSecretActive = t.previous_secret_active ?? false
      recentFires = t.recent_fires ?? []
      availableGroups = data.available_groups ?? []
      availableReviewers = data.available_reviewers ?? []
    } catch {
      fetchError = 'Network error — please reload.'
    } finally {
      loading = false
    }
  })

  // ── Submit ────────────────────────────────────────────────────────────────
  async function handleSubmit() {
    submitting = true
    submitError = null

    const url = isNew ? '/triggers.json' : `/triggers/${hashid}.json`
    const method = isNew ? 'POST' : 'PATCH'

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({
          trigger: {
            name,
            group_ids: groupIds,
            reviewer_id: reviewerId,
            prompt,
            audience_description: audienceDescription,
            active,
            source_type: sourceType,
            source_config: sourceConfig,
          },
        }),
      })

      const data = await res.json()

      if (res.ok) {
        if (isNew) {
          window.location.assign(`/triggers/${data.trigger.id}/next_steps`)
        } else {
          ;(window as any).toastr?.success('Trigger updated', '', { toastClass: 'toast trig-toast-plain' })
        }
      } else {
        submitError = (data.errors ?? []).join(', ') || 'Something went wrong.'
      }
    } catch {
      submitError = 'Network error — please try again.'
    } finally {
      submitting = false
    }
  }

  // ── Copy to clipboard ─────────────────────────────────────────────────────
  async function copyWebhookUrl() {
    await navigator.clipboard.writeText(webhookUrl)
    copiedWebhook = true
    setTimeout(() => { copiedWebhook = false }, 2000)
  }

  async function copySecret() {
    await navigator.clipboard.writeText(signingSecret)
    copiedSecret = true
    setTimeout(() => { copiedSecret = false }, 2000)
  }

  // ── Regenerate secret ─────────────────────────────────────────────────────
  async function doRegenerateSecret() {
    regenerating = true
    try {
      const res = await fetch(`/triggers/${hashid}/regenerate_secret.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: '{}',
      })
      const data = await res.json()
      if (res.ok) {
        signingSecret = data.signing_secret
        previousSigningSecret = data.previous_signing_secret ?? null
        previousSecretExpiresAtIso = data.previous_secret_expires_at_iso ?? null
        previousSecretActive = !!data.previous_signing_secret
        secretVisible = true
      }
    } catch {
      // Non-fatal — let user retry
    } finally {
      regenerating = false
      showRegenerateConfirm = false
    }
  }

  // ── Expire previous secret ────────────────────────────────────────────────
  async function doExpirePreviousSecret() {
    expiringPreviousSecret = true
    try {
      const res = await fetch(`/triggers/${hashid}/expire_previous_secret.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: '{}',
      })
      if (res.ok) {
        previousSigningSecret = null
        previousSecretExpiresAtIso = null
        previousSecretActive = false
      }
    } catch {
      // Non-fatal — banner stays visible; user can retry
    } finally {
      expiringPreviousSecret = false
    }
  }

  function formatExpiresAt(iso: string | null): string {
    if (!iso) return ''
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso
    }
  }

  // ── Delete trigger ────────────────────────────────────────────────────────
  async function doDelete() {
    deleting = true
    deleteError = null
    try {
      const res = await fetch(`/triggers/${hashid}.json`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
      })
      if (res.ok || res.status === 204) {
        window.location.assign('/triggers')
      } else {
        const data = await res.json().catch(() => ({}))
        deleteError = data.error || `Delete failed (${res.status})`
        deleting = false
      }
    } catch {
      deleteError = 'Network error — please try again.'
      deleting = false
    }
  }

  function formatFiredAt(iso: string): string {
    try {
      return new Date(iso).toLocaleString()
    } catch {
      return iso
    }
  }

  const statusColors: Record<string, string> = {
    pending_review: '#f57f17',
    approved: '#2e7d32',
    discarded: '#888',
    expired: '#f57f17',
    failed: '#c62828',
  }
</script>

<div class="trig-edit">
  <Portal target="#cta-portal">
    <button
      type="button"
      class="btn btn-success btn-rounded"
      onclick={handleSubmit}
      disabled={submitting || loading}
    >
      {submitting ? (isNew ? 'Saving…' : 'Updating…') : (isNew ? 'Save' : 'Update')}
    </button>
  </Portal>

  {#if loading}
    <div class="trig-loading" aria-busy="true">
      <div class="trig-spinner"></div>
      <p>Loading...</p>
    </div>
  {:else if fetchError}
    <div class="trig-error" role="alert">{fetchError}</div>
  {:else}
    <form class="trig-form" onsubmit={(e) => { e.preventDefault(); handleSubmit() }}>

      <!-- Source picker (disabled in edit mode) -->
      <SourcePicker
        {sourceType}
        disabled={!isNew}
        onChange={(v) => { sourceType = v; sourceConfig = {} }}
      />

      <!-- Name -->
      <div class="trig-field">
        <label class="trig-label" for="trig-name">Name <span class="trig-required">*</span></label>
        <input
          id="trig-name"
          type="text"
          class="trig-input"
          bind:value={name}
          placeholder="e.g. Snow Day Alert"
          required
          disabled={submitting}
        />
      </div>

      <!-- Groups (multi-select, up to MAX_GROUPS; "All" is exclusive) -->
      <div class="trig-field">
        <label class="trig-label" for="trig-group-picker">
          Send to Groups <span class="trig-required">*</span>
          <span class="trig-hint">
            {#if allGroupSelected}
              "All" includes every contact in your account — it can't be combined with other groups.
            {:else}
              Up to {MAX_GROUPS} groups. Contacts in multiple groups get only one message.
            {/if}
          </span>
        </label>

        <div class="trig-chips" id="trig-group-picker">
          {#each groupIds as gid (gid)}
            {@const g = availableGroups.find((x) => x.id === gid)}
            {#if g}
              <span class="trig-chip" class:trig-chip--all={g.is_all_group}>
                <span class="trig-chip__name">{g.name}</span>
                <button
                  type="button"
                  class="trig-chip__remove"
                  aria-label={`Remove ${g.name}`}
                  disabled={submitting}
                  onclick={() => { groupIds = groupIds.filter((id) => id !== gid) }}
                >×</button>
              </span>
            {/if}
          {/each}

          {#if groupIds.length < MAX_GROUPS}
            <div class="trig-chip-picker">
              <button
                type="button"
                class="trig-chip-picker__btn"
                disabled={submitting}
                onclick={() => { groupPickerOpen = !groupPickerOpen }}
                aria-haspopup="listbox"
                aria-expanded={groupPickerOpen}
              >+ Add group</button>

              {#if groupPickerOpen}
                <ul class="trig-chip-picker__menu" role="listbox">
                  {#each availableGroups.filter((g) => !groupIds.includes(g.id)) as g (g.id)}
                    <li>
                      <button
                        type="button"
                        class="trig-chip-picker__option"
                        class:trig-chip-picker__option--all={g.is_all_group}
                        role="option"
                        aria-selected="false"
                        onclick={() => {
                          addGroup(g.id)
                          groupPickerOpen = false
                        }}
                      >
                        {g.name}
                        {#if g.is_all_group && groupIds.length > 0}
                          <span class="trig-chip-picker__hint">(replaces current selection)</span>
                        {:else if !g.is_all_group && allGroupSelected}
                          <span class="trig-chip-picker__hint">(replaces "All")</span>
                        {/if}
                      </button>
                    </li>
                  {:else}
                    <li class="trig-chip-picker__empty">No more groups available</li>
                  {/each}
                </ul>
              {/if}
            </div>
          {:else}
            <span class="trig-chip-picker__limit">
              {MAX_GROUPS}-group max
            </span>
          {/if}
        </div>
      </div>

      <!-- Reviewer -->
      <div class="trig-field">
        <label class="trig-label" for="trig-reviewer">Reviewer <span class="trig-required">*</span></label>
        <select
          id="trig-reviewer"
          class="trig-input trig-select"
          bind:value={reviewerId}
          disabled={submitting}
        >
          <option value={null} disabled>Select a reviewer…</option>
          {#each availableReviewers as r (r.id)}
            <option value={r.id}>{r.name || r.email}</option>
          {/each}
        </select>
      </div>

      {#if sourceType === 'webhook'}
        <WebhookConfig bind:config={sourceConfig} {isNew} />
      {:else if sourceType === 'email'}
        <EmailConfig bind:config={sourceConfig} inboundAddress={inboundEmailAddress} />
      {:else if sourceType === 'weather'}
        <WeatherConfig bind:config={sourceConfig} triggerHashid={hashid} />
      {/if}

      <!-- Prompt -->
      <div class="trig-field">
        <label class="trig-label" for="trig-prompt">
          Prompt <span class="trig-required">*</span>
          <span class="trig-hint">
            {#if sourceType === 'weather'}
              Tell us how to draft the SMS when a matching weather alert arrives.
            {:else if sourceType === 'email'}
              Tell us how to draft the SMS from the forwarded email.
            {:else}
              Tell us how to draft the SMS from the webhook payload.
            {/if}
          </span>
        </label>
        <textarea
          id="trig-prompt"
          class="trig-input trig-textarea"
          rows={5}
          bind:value={prompt}
          placeholder={promptPlaceholder}
          required
          disabled={submitting}
        ></textarea>
      </div>

      <!-- Audience description -->
      <div class="trig-field">
        <label class="trig-label" for="trig-audience">
          Audience Description
          <span class="trig-hint">Optional. Helps us tailor the message. Falls back to group name.</span>
        </label>
        <input
          id="trig-audience"
          type="text"
          class="trig-input"
          bind:value={audienceDescription}
          placeholder="e.g. Parents at Riverside Elementary"
          disabled={submitting}
        />
      </div>

      <!-- Active toggle -->
      <div class="trig-field trig-field--inline">
        <label class="trig-label" for="trig-active">Active</label>
        <label class="trig-toggle" aria-label="Toggle trigger active state">
          <input id="trig-active" type="checkbox" bind:checked={active} disabled={submitting} />
          <span class="trig-toggle__track"></span>
        </label>
        <span class="trig-toggle-hint">
          {#if sourceType === 'weather'}
            {active ? 'Watching for matching NWS alerts' : 'Paused — alerts are ignored'}
          {:else if sourceType === 'email'}
            {active ? 'Accepting forwarded email' : 'Paused — incoming email is ignored'}
          {:else}
            {active ? 'Webhook is accepting events' : 'Webhook is paused (returns 409)'}
          {/if}
        </span>
      </div>

      {#if submitError}
        <div class="trig-error" role="alert">{submitError}</div>
      {/if}

    </form>

    <!-- Webhook details (edit mode + webhook source only) -->
    {#if !isNew}
      {#if sourceType === 'webhook'}
      <section class="trig-section">
        <div class="trig-section__header">
          <h2 class="trig-section__title">Webhook</h2>
          <a href="/triggers/{hashid}/next_steps" class="trig-section__link">Setup instructions →</a>
        </div>

        <div class="trig-field">
          <label class="trig-label" for="trig-webhook-url">Webhook URL</label>
          <div class="trig-copy-row">
            <input
              id="trig-webhook-url"
              type="text"
              class="trig-input trig-input--mono"
              readonly
              value={webhookUrl}
            />
            <button type="button" class="trig-btn trig-btn--ghost trig-btn--sm" onclick={copyWebhookUrl}>
              {copiedWebhook ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>

        <div class="trig-field">
          <label class="trig-label" for="trig-secret">Signing Secret</label>
          <div class="trig-copy-row">
            <input
              id="trig-secret"
              type={secretVisible ? 'text' : 'password'}
              class="trig-input trig-input--mono"
              readonly
              value={signingSecret}
              autocomplete="off"
            />
            <button type="button" class="trig-btn trig-btn--ghost trig-btn--sm" onclick={() => secretVisible = !secretVisible}>
              {secretVisible ? 'Hide' : 'Reveal'}
            </button>
            <button type="button" class="trig-btn trig-btn--ghost trig-btn--sm" onclick={copySecret} disabled={!secretVisible}>
              {copiedSecret ? 'Copied!' : 'Copy'}
            </button>
            <button
              type="button"
              class="trig-btn trig-btn--danger-outline trig-btn--sm"
              onclick={() => { showRegenerateConfirm = true }}
            >
              Rotate
            </button>
          </div>
          <p class="trig-hint">
            Both the old and new secrets will work for 24 hours after rotation, giving you time to update your integration without downtime. You can also expire the old secret immediately afterward.
          </p>

          {#if previousSecretActive}
            <div class="trig-grace-banner" role="status">
              <span class="trig-grace-banner__icon">&#9888;</span>
              <div class="trig-grace-banner__body">
                <p class="trig-grace-banner__text">
                  The old signing secret still works until <strong>{formatExpiresAt(previousSecretExpiresAtIso)}</strong>.
                  Update your integration with the new secret, then click "Expire old secret now" for a clean cutover.
                </p>
                <button
                  type="button"
                  class="trig-btn trig-btn--ghost trig-btn--sm"
                  onclick={doExpirePreviousSecret}
                  disabled={expiringPreviousSecret}
                >
                  {expiringPreviousSecret ? 'Expiring…' : 'Expire old secret now'}
                </button>
              </div>
            </div>
          {/if}
        </div>
      </section>
      {/if}

      <!-- Danger zone -->
        <section class="trig-section trig-danger">
          <h2 class="trig-section__title trig-danger__title">Danger zone</h2>
          <div class="trig-danger__row">
            <div>
              <div class="trig-danger__label">Delete this trigger</div>
              <div class="trig-danger__hint">
                {#if sourceType === 'weather'}
                  The trigger stops watching NWS immediately. No new drafts will be
                  generated.
                {:else if sourceType === 'email'}
                  The inbound email address stops accepting mail immediately. Any
                  message sent to it will bounce.
                {:else}
                  The webhook URL stops working immediately. Any external system
                  posting to it will get a 404.
                {/if}
                Drafts already in review are preserved. This cannot be undone — if you
                only want to pause it, toggle Active off instead.
              </div>
            </div>
            <button
              type="button"
              class="trig-btn trig-btn--danger"
              onclick={() => { showDeleteConfirm = true }}
            >Delete trigger</button>
          </div>
          {#if deleteError}
            <div class="trig-error" role="alert">{deleteError}</div>
          {/if}
        </section>

      <!-- Recent fires -->
      {#if recentFires.length > 0}
        <section class="trig-section">
          <h2 class="trig-section__title">Recent Activity</h2>
          <table class="trig-table trig-table--compact">
            <thead>
              <tr>
                <th>When</th>
                <th>Status</th>
                <th>Message Preview</th>
              </tr>
            </thead>
            <tbody>
              {#each recentFires as fire (fire.id)}
                <tr>
                  <td class="trig-mono">{formatFiredAt(fire.fired_at_iso)}</td>
                  <td>
                    <span class="trig-status" style="color: {statusColors[fire.status] ?? '#555'}">
                      {fire.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td class="trig-preview">{fire.message_preview ?? '—'}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        </section>
      {/if}
    {/if}
  {/if}
</div>

<!-- Delete confirm dialog -->
{#if showDeleteConfirm}
  <div class="trig-overlay" role="dialog" aria-modal="true" aria-labelledby="delete-confirm-title">
    <div class="trig-sheet">
      <h2 id="delete-confirm-title" class="trig-sheet__title">Delete this trigger?</h2>
      <p class="trig-sheet__body">
        {#if sourceType === 'weather'}
          This trigger will <strong>stop watching NWS immediately</strong> — no new drafts
          will be generated.
        {:else if sourceType === 'email'}
          The inbound email address will <strong>stop accepting mail immediately</strong> —
          any message sent to it will bounce.
        {:else}
          The webhook URL will <strong>stop working immediately</strong> — any external
          system posting to it will get a 404.
        {/if}
        This cannot be undone. If you only want to stop accepting events for a while,
        close this dialog and toggle <strong>Active</strong> off instead.
      </p>
      <div class="trig-sheet__actions">
        <button
          type="button"
          class="trig-btn trig-btn--danger"
          onclick={doDelete}
          disabled={deleting}
        >
          {deleting ? 'Deleting…' : 'Yes, delete trigger'}
        </button>
        <button
          type="button"
          class="trig-btn trig-btn--ghost"
          onclick={() => { showDeleteConfirm = false }}
          disabled={deleting}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Regenerate confirm dialog -->
{#if showRegenerateConfirm}
  <div class="trig-overlay" role="dialog" aria-modal="true" aria-labelledby="regen-confirm-title">
    <div class="trig-sheet">
      <h2 id="regen-confirm-title" class="trig-sheet__title">Rotate signing secret?</h2>
      <p class="trig-sheet__body">
        Both the old and new secrets will work for <strong>24 hours</strong> so you can update your
        integration without downtime. You can also expire the old secret immediately afterward once
        your integration is updated.
      </p>
      <div class="trig-sheet__actions">
        <button
          type="button"
          class="trig-btn trig-btn--danger"
          onclick={doRegenerateSecret}
          disabled={regenerating}
        >
          {regenerating ? 'Rotating…' : 'Yes, rotate secret'}
        </button>
        <button
          type="button"
          class="trig-btn trig-btn--ghost"
          onclick={() => { showRegenerateConfirm = false }}
          disabled={regenerating}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  .trig-edit {
    max-width: 640px;
    padding: 1.5rem 0 4rem;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #1a1a1a;
  }

  .trig-edit__header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    margin-bottom: 1.75rem;
  }

  .trig-edit__title {
    font-size: 1.4rem;
    font-weight: 700;
    margin: 0;
  }

  .trig-link-back {
    color: #888;
    text-decoration: none;
    font-size: 0.88rem;

    &:hover { color: #4a90e2; text-decoration: underline; }
  }

  /* ── Loading / error ── */
  .trig-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 3rem 0;
    color: #888;

    p { margin: 0; font-size: 0.9rem; }
  }

  .trig-spinner {
    width: 28px;
    height: 28px;
    border: 3px solid #e0e0e0;
    border-top-color: #4a90e2;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .trig-error {
    background: #fdecea;
    border: 1px solid #ef9a9a;
    border-radius: 8px;
    color: #c62828;
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  /* ── Form ── */
  .trig-form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    margin-bottom: 2rem;
  }

  .trig-field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }

  .trig-section .trig-field + .trig-field {
    margin-top: 1.1rem;
  }

  .trig-field--inline {
    flex-direction: row;
    align-items: center;
    gap: 0.75rem;
  }

  .trig-label {
    font-size: 0.85rem;
    font-weight: 600;
    color: #444;
    display: block;
    margin-bottom: 0.2rem;
  }

  .trig-required { color: #c62828; margin-left: 0.15rem; }

  .trig-hint {
    display: block;
    font-weight: 400;
    color: #888;
    font-size: 0.8rem;
    margin-top: 0.2rem;
  }

  .trig-input {
    border: 1px solid #d0d0d0;
    border-radius: 7px;
    padding: 0.6rem 0.75rem;
    font-size: 0.95rem;
    font-family: inherit;
    background: #fff;
    transition: border-color 0.15s;
    width: 100%;
    box-sizing: border-box;

    &:focus {
      outline: none;
      border-color: #4a90e2;
      box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.15);
    }

    &:disabled {
      background: #f5f5f5;
      color: #777;
      cursor: not-allowed;
    }

    &[readonly] {
      background: #f9f9f9;
      cursor: default;
    }
  }

  .trig-input--mono { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 0.85rem; }

  .trig-select { appearance: auto; }

  /* Group chips + picker */
  .trig-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    align-items: center;
    padding: 0.4rem;
    border: 1px solid #d0d0d0;
    border-radius: 7px;
    background: #fff;
    min-height: 2.5rem;
  }

  .trig-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.25rem 0.4rem 0.25rem 0.6rem;
    background: #eaf3fc;
    color: #1a5fa8;
    border: 1px solid #c1ddf5;
    border-radius: 999px;
    font-size: 0.85rem;
    font-weight: 500;
  }

  .trig-chip__remove {
    border: none;
    background: transparent;
    color: #1a5fa8;
    cursor: pointer;
    font-size: 1.1rem;
    line-height: 1;
    padding: 0 0.15rem;
    border-radius: 50%;

    &:hover:not(:disabled) { background: rgba(26, 95, 168, 0.15); }
    &:disabled { cursor: not-allowed; opacity: 0.5; }
  }

  .trig-chip--all {
    background: #f3edff;
    color: #5a3aa8;
    border-color: #d6c8f5;

    .trig-chip__remove { color: #5a3aa8; }
    .trig-chip__remove:hover:not(:disabled) { background: rgba(90, 58, 168, 0.15); }
  }

  .trig-chip-picker {
    position: relative;
  }

  .trig-chip-picker__btn {
    border: 1px dashed #b0b0b0;
    background: transparent;
    color: #555;
    border-radius: 999px;
    padding: 0.25rem 0.7rem;
    font-size: 0.85rem;
    cursor: pointer;
    font-family: inherit;

    &:hover:not(:disabled) { background: #f5f5f5; border-color: #4a90e2; color: #4a90e2; }
    &:disabled { cursor: not-allowed; opacity: 0.5; }
  }

  .trig-chip-picker__menu {
    position: absolute;
    top: calc(100% + 0.3rem);
    left: 0;
    z-index: 20;
    list-style: none;
    margin: 0;
    padding: 0.25rem;
    min-width: 260px;
    width: max-content;
    max-width: min(420px, 90vw);
    max-height: 240px;
    overflow-y: auto;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    box-shadow: 0 4px 14px rgba(0,0,0,0.08);

    li { margin: 0; }
  }

  .trig-chip-picker__option {
    width: 100%;
    text-align: left;
    border: none;
    background: transparent;
    padding: 0.45rem 0.6rem;
    border-radius: 5px;
    font-size: 0.88rem;
    color: #2c3e50;
    cursor: pointer;
    font-family: inherit;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
    white-space: nowrap;

    &:hover { background: #f1f7fd; }
  }

  .trig-chip-picker__option--all {
    color: #5a3aa8;
    font-weight: 500;

    &:hover { background: #f5efff; }
  }

  .trig-chip-picker__hint {
    color: #888;
    font-size: 0.78rem;
    font-style: italic;
    font-weight: 400;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .trig-chip-picker__empty {
    padding: 0.45rem 0.6rem;
    color: #888;
    font-size: 0.85rem;
    font-style: italic;
  }

  .trig-chip-picker__limit {
    color: #888;
    font-size: 0.8rem;
    font-style: italic;
    padding: 0 0.35rem;
  }

  .trig-textarea {
    resize: vertical;
    min-height: 100px;
    line-height: 1.5;
  }

  .trig-toggle-hint { font-size: 0.82rem; color: #888; }

  /* Toggle switch */
  .trig-toggle {
    position: relative;
    display: inline-flex;
    align-items: center;
    cursor: pointer;

    input { position: absolute; opacity: 0; width: 0; height: 0; }
  }

  .trig-toggle__track {
    width: 38px;
    height: 22px;
    background: #ccc;
    border-radius: 11px;
    transition: background 0.2s;
    position: relative;
  }

  :global(.trig-toggle__track::after) {
    content: '';
    position: absolute;
    top: 3px;
    left: 3px;
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: 50%;
    transition: transform 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.25);
  }

  :global(.trig-toggle input:checked + .trig-toggle__track) {
    background: #2e7d32;
  }

  :global(.trig-toggle input:checked + .trig-toggle__track::after) {
    transform: translateX(16px);
  }

  .trig-form__actions {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding-top: 0.5rem;
  }

  @keyframes fade-in { from { opacity: 0 } to { opacity: 1 } }

  /* Plain-text toastr variant: keep the colored background but drop the
     type-icon. Triggered by `toastClass: 'toast trig-toast-plain'` per-call.
     :global() because #toast-container is rendered outside this component. */
  :global(#toast-container > .trig-toast-plain) {
    background-image: none !important;
    padding-left: 15px !important;
  }

  /* ── Buttons ── */
  .trig-btn {
    display: inline-block;
    padding: 0.55rem 1.1rem;
    border-radius: 6px;
    font-size: 0.9rem;
    font-weight: 600;
    cursor: pointer;
    border: none;
    text-decoration: none;
    transition: opacity 0.15s, background 0.15s;
    font-family: inherit;
    text-align: center;

    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }

  .trig-btn--primary { background: #4a90e2; color: #fff; &:not(:disabled):hover { background: #3a7fd0; } }
  .trig-btn--ghost   { background: transparent; color: #555; border: 1px solid #ccc; &:not(:disabled):hover { background: #f5f5f5; } }
  .trig-btn--danger  { background: #c62828; color: #fff; &:not(:disabled):hover { background: #b71c1c; } }
  .trig-btn--danger-outline { background: transparent; color: #c62828; border: 1px solid #c62828; &:not(:disabled):hover { background: #fdecea; } }
  .trig-btn--sm { padding: 0.35rem 0.75rem; font-size: 0.82rem; }

  /* ── Sections ── */
  .trig-section {
    border-top: 1px solid #eee;
    padding-top: 1.5rem;
    margin-top: 1.5rem;
  }

  .trig-section__title {
    font-size: 1rem;
    font-weight: 700;
    margin: 0 0 1.1rem;
    color: #1a1a1a;
  }

  .trig-section__header {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1.1rem;

    .trig-section__title {
      margin: 0;
    }
  }

  .trig-section__link {
    color: #4a90e2;
    font-size: 0.85rem;
    text-decoration: none;

    &:hover { text-decoration: underline; }
  }

  .trig-danger {
    border-top-color: #f4cdcd;
  }

  .trig-danger__title {
    color: #c62828;
  }

  .trig-danger__row {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1rem;
    border: 1px solid #f4cdcd;
    border-radius: 8px;
    background: #fdf5f5;
  }

  .trig-danger__label {
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 0.25rem;
  }

  .trig-danger__hint {
    color: #555;
    font-size: 0.85rem;
    line-height: 1.45;
    max-width: 38rem;
  }

  .trig-copy-row {
    display: flex;
    gap: 0.5rem;
    align-items: center;

    .trig-input { flex: 1; min-width: 0; }
  }

  .trig-warning {
    font-size: 0.8rem;
    color: #f57f17;
    margin: 0.4rem 0 0;
  }

  /* Grace period banner */
  .trig-grace-banner {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    background: #fff8e1;
    border: 1px solid #ffe082;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    margin-top: 0.75rem;
  }

  .trig-grace-banner__icon {
    font-size: 1rem;
    color: #f57f17;
    flex-shrink: 0;
    margin-top: 0.1rem;
  }

  .trig-grace-banner__body {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex: 1;
  }

  .trig-grace-banner__text {
    margin: 0;
    font-size: 0.85rem;
    color: #5d4037;
    line-height: 1.45;
  }

  /* ── Tables ── */
  .trig-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.88rem;

    th {
      text-align: left;
      padding: 0.5rem 0.6rem;
      border-bottom: 2px solid #e5e5e5;
      color: #555;
      font-weight: 600;
      font-size: 0.78rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }

    td {
      padding: 0.55rem 0.6rem;
      border-bottom: 1px solid #f0f0f0;
      vertical-align: middle;
    }
  }

  .trig-table--compact td { padding: 0.4rem 0.6rem; }

  .trig-mono { font-family: 'SF Mono', 'Fira Code', monospace; font-size: 0.8rem; }
  .trig-status { font-weight: 500; font-size: 0.82rem; }
  .trig-preview { color: #555; max-width: 280px; }

  /* ── Overlay / sheet ── */
  .trig-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 1000;
    animation: overlay-in 0.18s ease;

    @media (min-width: 520px) { align-items: center; }
  }

  @keyframes overlay-in { from { opacity: 0 } to { opacity: 1 } }

  .trig-sheet {
    background: #fff;
    border-radius: 16px 16px 0 0;
    padding: 1.5rem 1.25rem 2rem;
    width: 100%;
    max-width: 480px;
    animation: sheet-up 0.2s cubic-bezier(0.22, 1, 0.36, 1);

    @media (min-width: 520px) { border-radius: 16px; }
  }

  @keyframes sheet-up {
    from { transform: translateY(24px); opacity: 0; }
    to   { transform: translateY(0);    opacity: 1; }
  }

  .trig-sheet__title {
    font-size: 1.1rem;
    font-weight: 700;
    margin: 0 0 0.6rem;
    color: #1a1a1a;
  }

  .trig-sheet__body {
    font-size: 0.92rem;
    line-height: 1.5;
    color: #444;
    margin: 0 0 1.25rem;
  }

  .trig-sheet__actions {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;

    .trig-btn { width: 100%; display: block; }
  }
</style>
